import styles from './choose-your-maintenance.module.css'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import { useMaintenanceContext } from '../../context/maintenance-context'
import {
  Checkbox,
  Text,
  Alert,
  SvgIcon,
  TextLink,
  Textarea,
} from '@cinch-labs/design-system'
import { useEffect, useRef, useState } from 'react'
import { serviceOptionDefaults } from '../../types'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { useServiceOptionsExplainedDrawer } from './service-options-explained'
import { datadogRum } from '@datadog/browser-rum'

const isElectricVehicle = (fuelType?: string): boolean => {
  if (!fuelType) return false
  return fuelType.toLowerCase() === 'electric'
}

export const ChooseYourMaintenance: React.FC = () => {
  const {
    vehicleMake,
    vehicleModel,
    vehicleFuelType,
    vrm,
    store,
    handleNext,
    dispatch,
    step,
    serviceOptions,
    notes: contextNotes,
    canBookAppointment,
  } = useMaintenanceContext()

  const [mot, setMot] = useState<boolean>(false)
  const [oilAndFilter, setOilAndFilter] = useState<boolean>(false)
  const [standardService, setStandardService] = useState<boolean>(false)
  const [majorService, setMajorService] = useState<boolean>(false)
  const [notes, setNotes] = useState<string>(contextNotes || '')
  const [error, setError] = useState<string>()
  const isCheckboxesSet = useRef(false)
  const servicePageLoadEventSent = useRef(false)
  const { trackEvent } = useAnalytics()

  const { setShowServiceOptionsExplainedDrawer, setIsEv } =
    useServiceOptionsExplainedDrawer()

  const isEV = isElectricVehicle(vehicleFuelType)

  useEffect(() => {
    setIsEv(isEV)
  }, [isEV, setIsEv])

  useEffect(() => {
    if (servicePageLoadEventSent.current === false) {
      const loadChooseServicePage = {
        eventName: `Select date time`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.load,
            category: 'cinch_servicing',
            action: 'choose_service_page_load',
            label: canBookAppointment ? 'booking_journey' : 'enquiry_journey',
          },
        },
      }

      trackEvent(loadChooseServicePage)
    }

    servicePageLoadEventSent.current = true
  }, [servicePageLoadEventSent])

  const title = 'Choose your service'
  const description = `For your ${vehicleMake} ${vehicleModel} (${vrm})${
    store ? ` at ${store}` : ''
  }`

  const onContinue = () => {
    if (mot || oilAndFilter || standardService || majorService) {
      const serviceOptions = {
        ...serviceOptionDefaults,
        ...(mot
          ? { mot: { ...serviceOptionDefaults.mot, selected: true } }
          : {}),
        ...(oilAndFilter && !isEV
          ? {
              oilAndFilter: {
                ...serviceOptionDefaults.oilAndFilter,
                selected: true,
              },
            }
          : {}),
        ...(standardService
          ? isEV
            ? {
                standardServiceEV: {
                  ...serviceOptionDefaults.standardServiceEV,
                  selected: true,
                },
              }
            : {
                standardService: {
                  ...serviceOptionDefaults.standardService,
                  selected: true,
                },
              }
          : {}),
        ...(majorService
          ? isEV
            ? {
                majorServiceEV: {
                  ...serviceOptionDefaults.majorServiceEV,
                  selected: true,
                },
              }
            : {
                majorService: {
                  ...serviceOptionDefaults.majorService,
                  selected: true,
                },
              }
          : {}),
      }

      const serviceOptionsSelectedNames = Object.values(serviceOptions)
        .reduce((agg, cur) => (cur.selected ? agg + cur.name + ', ' : agg), '')
        .replace(/, $/, '')

      dispatch({
        type: 'SET_SELECTED_SERVICE_OPTIONS',
        serviceOptions,
        serviceOptionsSelectedNames,
        serviceOptionsSelectedTotalPrice: Object.values(serviceOptions ?? {})
          .reduce((agg, cur) => (cur.selected ? agg + cur.price : agg), 0)
          .toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      })

      // Save notes
      dispatch({ type: 'UPDATE_NOTES', notes })

      const chooseMaintenance = {
        eventName: `Select date time`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.click,
            category: 'cinch_servicing',
            action: 'service_type_selected',
            label: canBookAppointment ? 'booking_journey' : 'enquiry_journey',
            type: serviceOptionsSelectedNames,
          },
        },
      }

      trackEvent(chooseMaintenance)

      datadogRum.addAction('Cinch Servicing: Service Selected', {
        cinchServicing: true,
        step,
        page: window.location.pathname,
        serviceOptionsSelectedNames,
        notesLength: notes.length,
        hasNotes: notes.length > 0,
        canBookAppointment,
      })

      handleNext()
    } else {
      setError('Please select at least one option')
    }
  }

  if (!isCheckboxesSet.current) {
    setOilAndFilter(serviceOptions?.oilAndFilter.selected ?? false)
    setMot(serviceOptions?.mot.selected ?? false)
    setStandardService(serviceOptions?.standardService.selected ?? false)
    setMajorService(serviceOptions?.majorService.selected ?? false)
    isCheckboxesSet.current = true
  }

  const serviceOptionsExplainedButtonText = 'Service options explained'

  return (
    <CarMaintenanceStepWrapper
      title={title}
      description={description}
      onContinue={onContinue}
    >
      <div className={styles.optionsContainer}>
        <Text element="h2" fontSizeFixed="m" fontWeight="bold">
          Choose at least one option
        </Text>
        <Checkbox
          label={`${serviceOptionDefaults.mot.name} - £${serviceOptionDefaults.mot.price}`}
          id="mot"
          name="mot"
          checked={mot}
          onChange={() => {
            setMot((mot) => !mot)
            setError('')
          }}
          error={{ show: false, message: 'This needs to be checked' }}
          description="(Every 12 Months)"
          classNames={{
            wrapper: styles.checkbox,
            label: 'label',
            icon: 'icon',
          }}
          disabled={false}
        />
        {!isEV && (
          <Checkbox
            label={`${serviceOptionDefaults.oilAndFilter.name} - £${serviceOptionDefaults.oilAndFilter.price}`}
            id="oil"
            name="oil"
            checked={oilAndFilter}
            onChange={() => {
              setOilAndFilter((oil) => !oil)
              setError('')
            }}
            description="(Every 5-7,000 miles)"
            classNames={{
              wrapper: styles.checkbox,
            }}
            disabled={false}
          />
        )}
        <Checkbox
          label={`${
            isEV
              ? serviceOptionDefaults.standardServiceEV.name
              : serviceOptionDefaults.standardService.name
          } - £${
            isEV
              ? serviceOptionDefaults.standardServiceEV.price
              : serviceOptionDefaults.standardService.price
          }`}
          id="standard"
          name="standard"
          checked={standardService}
          onChange={() => {
            setStandardService((standardService) => !standardService)
            setError('')
          }}
          description="(Every 10-12,000 miles)"
          classNames={{
            wrapper: styles.checkbox,
            label: 'label',
            icon: 'icon',
          }}
          disabled={false}
        />
        <Checkbox
          label={`${
            isEV
              ? serviceOptionDefaults.majorServiceEV.name
              : serviceOptionDefaults.majorService.name
          } - £${
            isEV
              ? serviceOptionDefaults.majorServiceEV.price
              : serviceOptionDefaults.majorService.price
          }`}
          id="major"
          name="major"
          checked={majorService}
          onChange={() => {
            setMajorService((majorService) => !majorService)
            setError('')
          }}
          description="(Every 20-24,000 miles)"
          classNames={{
            wrapper: styles.checkbox,
            label: 'label',
          }}
          disabled={false}
        />
      </div>
      <div className={styles.serviceOptionsLink}>
        <div className={styles.infoIcon}>
          <SvgIcon name="info" color="toreaBay" height={16} />
        </div>
        <TextLink
          element={'a'}
          textProps={{ fontSizeFixed: 's', fontWeight: 'semibold' }}
          onClick={() => {
            const serviceOptionsExplainedEvent = {
              eventName: `Service options explained drawer opened`,
              type: TrackingEventTypes.ADOBE,
              data: {
                event: {
                  name: EventNames.click,
                  category: 'cinch_servicing',
                  action: 'open',
                  label: 'service-options-explained',
                  type: 'drawer',
                },
              },
            }
            trackEvent(serviceOptionsExplainedEvent)
            setShowServiceOptionsExplainedDrawer(true)
          }}
        >
          {serviceOptionsExplainedButtonText}
        </TextLink>
      </div>
      <div className={styles.bookingNotesContainer}>
        <Text
          element="h3"
          fontSizeFixed="m"
          fontWeight="bold"
          className={styles.bookingNotesTitle}
        >
          Booking notes (optional)
        </Text>
        <Text fontSizeFixed="s" className={styles.bookingNotesDescription}>
          Is there anything specific you'd like us to check?
        </Text>
        <Textarea
          id="booking-notes"
          className={styles.bookingNotesTextarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. warning lights, unusual noise"
          rows={6}
          maxLength={300}
        />
      </div>
      {error && <Alert>{error}</Alert>}
    </CarMaintenanceStepWrapper>
  )
}
